import { type ImageLoader, type ImageLoaderProps } from 'next/image';

import { type UploadcareImageOperation } from '../components/Image/DefaultImageComponent';

export type UploadcareImageLoaderOptions = {
  alignment?: string;
  aspectRatio: string;
  operation: UploadcareImageOperation;
};

const RESIZE_OPERATION_PATTERN = /\/-\/resize\/(\d+)x\//g;
const ASPECT_RATIO_PATTERN = /^(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)$/;
const ALIGNMENT_PATTERN = /^(\d+(?:\.\d+)?)p,(\d+(?:\.\d+)?)p$/;

const getSafeAlignment = (alignment?: string): string => {
  if (!alignment || alignment === 'center') return 'center';

  const match = ALIGNMENT_PATTERN.exec(alignment);
  const x = Number(match?.[1]);
  const y = Number(match?.[2]);

  return Number.isFinite(x) && Number.isFinite(y) && x >= 0 && x <= 100 && y >= 0 && y <= 100 ? alignment : 'center';
};

export const transformUploadcareLoaderUrl = (url: string, { alignment, aspectRatio, operation }: UploadcareImageLoaderOptions): string => {
  const aspectRatioMatch = ASPECT_RATIO_PATTERN.exec(aspectRatio);
  const ratioWidth = Number(aspectRatioMatch?.[1]);
  const ratioHeight = Number(aspectRatioMatch?.[2]);

  if (!Number.isFinite(ratioWidth) || !Number.isFinite(ratioHeight) || ratioWidth <= 0 || ratioHeight <= 0) {
    return url;
  }

  const resizeMatches = [...url.matchAll(RESIZE_OPERATION_PATTERN)];
  const resizeMatch = resizeMatches.at(-1);
  const matchedOperation = resizeMatch?.[0];
  const matchedWidth = Number(resizeMatch?.[1]);

  if (!resizeMatch || !matchedOperation || !Number.isFinite(matchedWidth) || matchedWidth <= 0) {
    return url;
  }

  const height = Math.max(1, Math.round((matchedWidth * ratioHeight) / ratioWidth));
  const safeAlignment = getSafeAlignment(alignment);
  const transformation =
    operation === 'scale_crop' ? `/-/scale_crop/${matchedWidth}x${height}/${safeAlignment}/` : `/-/preview/${matchedWidth}x${height}/`;
  const matchIndex = resizeMatch.index;

  return `${url.slice(0, matchIndex)}${transformation}${url.slice(matchIndex + matchedOperation.length)}`;
};

export const createUploadcareImageLoader =
  (baseLoader: ImageLoader, options: UploadcareImageLoaderOptions) =>
  (props: ImageLoaderProps): string =>
    transformUploadcareLoaderUrl(baseLoader(props), options);
